import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import { listClientProfiles } from "services/tenantUserAdminService";
import { TC } from "../../../tokens/text";

export default function RecentClientsWidget({ tenantId }) {
  const history = useHistory();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  const handleClientOpen = (clientId) => {
    history.push(`/client-profiles/${clientId}`);
  };

  const handleClientKeyDown = (event, clientId) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleClientOpen(clientId);
    }
  };

  useEffect(() => {
    if (!tenantId) return;
    setLoading(true);
    listClientProfiles({ tenantId })
      .then((res) => {
        const sorted = (res.clientProfiles || [])
          .filter(p => p.status !== "archived")
          .sort((a, b) => {
            const aTime = a.createdAt?.seconds || a.createdAt || 0;
            const bTime = b.createdAt?.seconds || b.createdAt || 0;
            return bTime - aTime;
          })
          .slice(0, 5);
        setClients(sorted);
      })
      .catch(() => setClients([]))
      .finally(() => setLoading(false));
  }, [tenantId]);

  if (loading) {
    return (
      <VuiBox display="flex" justifyContent="center" alignItems="center" height="100%" py={4}>
        <CircularProgress size={28} />
      </VuiBox>
    );
  }

  if (clients.length === 0) {
    return (
      <VuiBox display="flex" justifyContent="center" alignItems="center" height="100%" py={4}>
        <VuiTypography color="text" variant="button">No clients yet</VuiTypography>
      </VuiBox>
    );
  }

  return (
    <VuiBox p={1.5} display="flex" flexDirection="column" gap={0.5}>
      {clients.map((client) => {
        const clientName = client.name || client.companyName || client.id;

        return (
          <VuiBox
            key={client.id}
            display="flex"
            alignItems="center"
            gap={1.5}
            px={1.5}
            py={1}
            role="button"
            tabIndex={0}
            sx={{
              borderRadius: "10px",
              cursor: "pointer",
              "&:hover": { background: "rgba(0,117,255,0.06)" },
              transition: "background 0.15s",
            }}
            onClick={() => handleClientOpen(client.id)}
            onKeyDown={(event) => handleClientKeyDown(event, client.id)}
          >
            <VuiBox sx={{ flex: 1, minWidth: 0 }}>
              <VuiTypography
                sx={{ color: TC.primary, fontSize: 13, fontWeight: 600,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              >
                {clientName}
              </VuiTypography>
            </VuiBox>
            <Chip
              label={client.clientType === "individual" ? "Individual" : "Company"}
              size="small"
              sx={{
                bgcolor: client.clientType === "individual" ? "rgba(76,175,80,0.15)" : "rgba(0,117,255,0.15)",
                color: client.clientType === "individual" ? "#4caf50" : "#90caf9",
                fontSize: "0.62rem",
                height: 18,
                border: `1px solid ${client.clientType === "individual" ? "rgba(76,175,80,0.3)" : "rgba(0,117,255,0.3)"}`,
              }}
            />
            <IconButton
              size="small"
              aria-label={`Open ${clientName} client`}
              onClick={(e) => { e.stopPropagation(); handleClientOpen(client.id); }}
              sx={{ p: 0.3, color: TC.tertiary, "&:hover": { color: TC.linkHover } }}
            >
              <OpenInNewIcon aria-hidden="true" sx={{ fontSize: "0.85rem" }} />
            </IconButton>
          </VuiBox>
        );
      })}
    </VuiBox>
  );
}

